// src/components/Modals/ModalProgramacion/ModalConfigMatch.jsx
import React, { useEffect, useMemo, useState } from "react";
import Modal from "@mui/joy/Modal";
import ModalClose from "@mui/joy/ModalClose";
import Sheet from "@mui/joy/Sheet";
import Typography from "@mui/joy/Typography";
import axios from "axios";
import { toast } from "react-hot-toast";
import API_BASE_URL from "../../../config/config.js";

const emptyForm = {
    // Acumulado
    nro_grupo1: "",
    nro_pos1: "",
    nro_pos_acum1: "",
    nro_grupo2: "",
    nro_pos2: "",
    nro_pos_acum2: "",
    // Avance winner
    idNextMatchWin: "",
    posNextMatchWin: "",
    // Avance loser
    idNextMatchLose: "",
    posNextMatchLose: "",
};

const ModalConfigMatch = ({ open, setOpen, idmatch, idevent, idsport }) => {
    const [loading, setLoading] = useState(false);
    const [config, setConfig] = useState(null);
    const [form, setForm] = useState(emptyForm);
    const hasConfig = useMemo(() => Boolean(config?.idmatches_config), [config]);

    const fetchConfig = async () => {
        try {
            setLoading(true);
            const { data } = await axios.post(`${API_BASE_URL}/getmatchesconfig`, { idmatch });
            const item = Array.isArray(data) ? data[0] : null;
            setConfig(item || null);

            if (item) {
                setForm({
                    // acumulado
                    nro_grupo1: item.nro_grupo1 ?? "",
                    nro_pos1: item.nro_pos1 ?? "",
                    nro_pos_acum1: item.nro_pos_acum1 ?? "",
                    nro_grupo2: item.nro_grupo2 ?? "",
                    nro_pos2: item.nro_pos2 ?? "",
                    nro_pos_acum2: item.nro_pos_acum2 ?? "",
                    // winner
                    idNextMatchWin: item.idNextMatchWin ?? "",
                    posNextMatchWin: item.posNextMatchWin ?? "",
                    // loser
                    idNextMatchLose: item.idNextMatchLose ?? "",
                    posNextMatchLose: item.posNextMatchLose ?? "",
                });
            } else {
                setForm(emptyForm);
            }
        } catch (err) {
            console.error(err);
            toast.error("No se pudo cargar la configuración del match.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (open && idmatch) fetchConfig();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, idmatch]);

    const onChange = (e) => {
        const { name, value } = e.target;
        const numericFields = [
            "nro_grupo1",
            "nro_pos1",
            "nro_pos_acum1",
            "nro_grupo2",
            "nro_pos2",
            "nro_pos_acum2",
            "idNextMatchWin",
            "posNextMatchWin",
            "idNextMatchLose",
            "posNextMatchLose",
        ];
        if (numericFields.includes(name)) {
            if (value === "" || /^[0-9]+$/.test(value)) {
                setForm((p) => ({ ...p, [name]: value }));
            }
        } else {
            setForm((p) => ({ ...p, [name]: value }));
        }
    };

    const handleCreate = async () => {
        try {
            setLoading(true);
            await axios.post(`${API_BASE_URL}/postmatchesconfig`, {
                idmatch,
                idevent: Number(idevent),
                idsport: Number(idsport),
            });
            toast.success("Config creada.");
            await fetchConfig();
        } catch (err) {
            console.error(err);
            toast.error("No se pudo crear la config.");
        } finally {
            setLoading(false);
        }
    };

    const handleUpdate = async (e) => {
        e?.preventDefault?.();
        if (!config?.idmatches_config) return;

        const toInt = (v) => (v === "" ? null : Number(v));

        const payload = {
            idmatches_config: config.idmatches_config,
            // acumulado
            nro_grupo1: toInt(form.nro_grupo1),
            nro_pos1: toInt(form.nro_pos1),
            nro_pos_acum1: toInt(form.nro_pos_acum1),
            nro_grupo2: toInt(form.nro_grupo2),
            nro_pos2: toInt(form.nro_pos2),
            nro_pos_acum2: toInt(form.nro_pos_acum2),
            // winner
            idNextMatchWin: toInt(form.idNextMatchWin),
            posNextMatchWin: toInt(form.posNextMatchWin),
            // loser
            idNextMatchLose: toInt(form.idNextMatchLose),
            posNextMatchLose: toInt(form.posNextMatchLose),
        };

        try {
            setLoading(true);
            await axios.patch(`${API_BASE_URL}/patchmatchesconfig`, payload);
            toast.success("Config actualizada.");
            await fetchConfig();
        } catch (err) {
            console.error(err);
            toast.error("No se pudo actualizar la config.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal
            aria-labelledby="modal-config-title"
            aria-describedby="modal-config-desc"
            open={open}
            onClose={() => setOpen(false)}
            sx={{ display: "flex", justifyContent: "center", alignItems: "center", p: 2 }}
        >
            {/* Contenedor con límites de ancho/alto y scroll de contenido */}
            <Sheet
                variant="outlined"
                className="
          w-[min(96vw,1000px)]
          max-h-[85vh]
          bg-white
          rounded-2xl
          shadow-xl
          overflow-hidden
          flex flex-col
        "
            >
                {/* Header sticky */}
                <div className="sticky top-0 z-10 bg-white/90 backdrop-blur border-b">
                    <div className="px-5 py-3 flex items-center justify-between">
                        <Typography id="modal-config-title" level="h4" className="text-xl sm:text-2xl font-semibold">
                            Configuración de Match
                        </Typography>
                        <div className="flex items-center gap-2">
                            <span className="text-[11px] sm:text-xs px-2 py-1 rounded-full bg-gray-100 text-gray-600 mr-5">
                                ID Match: {idmatch}
                            </span>
                            <ModalClose className="m-0 ml-2" />
                        </div>
                    </div>
                </div>

                {/* Contenido con scroll */}
                <div className="flex-1 overflow-y-auto px-5 py-4">
                    {loading && (
                        <div className="flex items-center gap-3 text-gray-600">
                            <span className="h-3 w-3 rounded-full bg-gray-300 animate-pulse" />
                            Cargando...
                        </div>
                    )}

                    {!loading && !hasConfig && (
                        <div className="rounded-xl border border-dashed p-5 text-center">
                            <p className="text-gray-700">Aún no existe una configuración para este encuentro.</p>
                            <button
                                onClick={handleCreate}
                                className="mt-4 inline-flex items-center gap-2 rounded-full bg-indigo-600 px-5 py-2 text-white shadow hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                            >
                                <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M12 5v14M5 12h14" />
                                </svg>
                                Crear Config Match
                            </button>
                        </div>
                    )}

                    {!loading && hasConfig && (
                        <form onSubmit={handleUpdate} className="mt-1 space-y-6">
                            {/* ID config */}
                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                                <div>
                                    <label className="text-sm text-gray-700">idmatches_config</label>
                                    <input
                                        value={config.idmatches_config}
                                        disabled
                                        className="mt-1 w-full rounded-lg border border-gray-300 bg-gray-100 p-2 text-gray-700"
                                    />
                                </div>
                            </div>

                            {/* Clasificación por acumulado */}
                            <div className="rounded-xl border p-4">
                                <div className="flex items-center justify-between mb-3">
                                    <h3 className="font-semibold text-gray-800">Clasificación automática (Acumulado)</h3>
                                    <span className="text-xs text-gray-500">nro_grupo / nro_pos / nro_pos_acum</span>
                                </div>

                                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                                    <div className="min-w-0">
                                        <label className="text-sm text-gray-700">nro_grupo1</label>
                                        <input
                                            name="nro_grupo1"
                                            value={form.nro_grupo1}
                                            onChange={onChange}
                                            className="mt-1 w-full rounded-lg border border-gray-300 p-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                            placeholder="ej. 1"
                                        />
                                    </div>
                                    <div className="min-w-0">
                                        <label className="text-sm text-gray-700">nro_pos1</label>
                                        <input
                                            name="nro_pos1"
                                            value={form.nro_pos1}
                                            onChange={onChange}
                                            className="mt-1 w-full rounded-lg border border-gray-300 p-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                            placeholder="ej. 1"
                                        />
                                    </div>
                                    <div className="min-w-0">
                                        <label className="text-sm text-gray-700">nro_pos_acum1</label>
                                        <input
                                            name="nro_pos_acum1"
                                            value={form.nro_pos_acum1}
                                            onChange={onChange}
                                            className="mt-1 w-full rounded-lg border border-gray-300 p-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                            placeholder="ej. 5"
                                        />
                                    </div>

                                    <div className="min-w-0">
                                        <label className="text-sm text-gray-700">nro_grupo2</label>
                                        <input
                                            name="nro_grupo2"
                                            value={form.nro_grupo2}
                                            onChange={onChange}
                                            className="mt-1 w-full rounded-lg border border-gray-300 p-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                            placeholder="ej. 2"
                                        />
                                    </div>
                                    <div className="min-w-0">
                                        <label className="text-sm text-gray-700">nro_pos2</label>
                                        <input
                                            name="nro_pos2"
                                            value={form.nro_pos2}
                                            onChange={onChange}
                                            className="mt-1 w-full rounded-lg border border-gray-300 p-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                            placeholder="ej. 2"
                                        />
                                    </div>
                                    <div className="min-w-0">
                                        <label className="text-sm text-gray-700">nro_pos_acum2</label>
                                        <input
                                            name="nro_pos_acum2"
                                            value={form.nro_pos_acum2}
                                            onChange={onChange}
                                            className="mt-1 w-full rounded-lg border border-gray-300 p-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                            placeholder="ej. 6"
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Avance Winner */}
                            <div className="rounded-xl border p-4">
                                <div className="flex items-center justify-between mb-3">
                                    <h3 className="font-semibold text-green-700">Avance (Winner)</h3>
                                    <span className="text-xs text-gray-500">pos: 1 → idparticipant1, 2 → idparticipant2</span>
                                </div>

                                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                                    <div className="sm:col-span-2 min-w-0">
                                        <label className="text-sm text-gray-700">idNextMatchWin</label>
                                        <input
                                            name="idNextMatchWin"
                                            value={form.idNextMatchWin}
                                            onChange={onChange}
                                            className="mt-1 w-full rounded-lg border border-gray-300 p-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                            placeholder="ID match destino (entero)"
                                        />
                                    </div>
                                    <div className="min-w-0">
                                        <label className="text-sm text-gray-700">posNextMatchWin</label>
                                        <input
                                            name="posNextMatchWin"
                                            value={form.posNextMatchWin}
                                            onChange={onChange}
                                            className="mt-1 w-full rounded-lg border border-gray-300 p-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                            placeholder="1 o 2"
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Avance Loser */}
                            <div className="rounded-xl border p-4">
                                <div className="flex items-center justify-between mb-3">
                                    <h3 className="font-semibold text-red-700">Avance (Loser)</h3>
                                    <span className="text-xs text-gray-500">pos: 1 → idparticipant1, 2 → idparticipant2</span>
                                </div>

                                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                                    <div className="sm:col-span-2 min-w-0">
                                        <label className="text-sm text-gray-700">idNextMatchLose</label>
                                        <input
                                            name="idNextMatchLose"
                                            value={form.idNextMatchLose}
                                            onChange={onChange}
                                            className="mt-1 w-full rounded-lg border border-gray-300 p-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                            placeholder="ID match destino (entero)"
                                        />
                                    </div>
                                    <div className="min-w-0">
                                        <label className="text-sm text-gray-700">posNextMatchLose</label>
                                        <input
                                            name="posNextMatchLose"
                                            value={form.posNextMatchLose}
                                            onChange={onChange}
                                            className="mt-1 w-full rounded-lg border border-gray-300 p-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                            placeholder="1 o 2"
                                        />
                                    </div>
                                </div>
                            </div>
                        </form>
                    )}
                </div>

                {/* Footer sticky */}
                <div className="sticky bottom-0 z-10 bg-white/90 backdrop-blur border-t">
                    <div className="px-5 py-3 flex items-center justify-end gap-3">
                        <button
                            type="button"
                            onClick={() => setOpen(false)}
                            className="rounded-full border border-gray-300 px-4 py-2 text-gray-700 hover:bg-gray-50"
                        >
                            Cerrar
                        </button>
                        {hasConfig && (
                            <button
                                onClick={handleUpdate}
                                className="inline-flex items-center gap-2 rounded-full bg-indigo-600 px-5 py-2 text-white shadow hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                            >
                                Actualizar
                            </button>
                        )}
                        {!hasConfig && (
                            <button
                                onClick={handleCreate}
                                className="inline-flex items-center gap-2 rounded-full bg-indigo-600 px-5 py-2 text-white shadow hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                            >
                                Crear
                            </button>
                        )}
                    </div>
                </div>
            </Sheet>
        </Modal>
    );
};

export default ModalConfigMatch;
